import { z } from 'zod'
import { Action } from './actions.js'
import { Comparison, Id, Milliseconds, Scope, VariableValue } from './primitives.js'
import { Trigger } from './triggers.js'

const VariableCondition = z.object({
  type: z.literal('variable'),
  name: z.string().max(64),
  comparison: Comparison,
  value: VariableValue,
})

/** Minutes since local midnight at the placement, so worlds can keep hours. */
const TimeWindowCondition = z.object({
  type: z.literal('timeWindow'),
  startMinute: z.number().int().min(0).max(1439),
  endMinute: z.number().int().min(0).max(1439),
})

const RoleCondition = z.object({
  type: z.literal('role'),
  role: z.enum(['listener', 'broadcaster', 'operator', 'editor']),
})

export const Condition = z.discriminatedUnion('type', [
  VariableCondition,
  TimeWindowCondition,
  RoleCondition,
])
export type Condition = z.infer<typeof Condition>

/**
 * How often a rule may fire. `once` means once for the world across all time,
 * which only makes sense for session-scoped rules; the listener-scoped
 * equivalents are the per-listener limits.
 */
export const FireLimit = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unlimited') }),
  z.object({ kind: z.literal('once') }),
  z.object({ kind: z.literal('oncePerSession') }),
  z.object({ kind: z.literal('oncePerListener') }),
  z.object({ kind: z.literal('count'), n: z.number().int().positive() }),
])
export type FireLimit = z.infer<typeof FireLimit>

/**
 * Trigger, optional conditions, ordered actions. The unit of cause and effect.
 *
 * `scope` may be left as `auto`, in which case publishing classifies the rule
 * from its triggers: anything that depends on the wall clock, an operator, or
 * other participants must run on the server, and everything else runs on the
 * listener's device where it can fire in milliseconds and survive a dropout.
 */
export const Rule = z.object({
  id: Id,
  name: z.string().max(120),
  enabled: z.boolean().default(true),
  scope: z.union([Scope, z.literal('auto')]).default('auto'),
  triggers: z.array(Trigger).min(1),
  /** With `all`, every trigger must have fired inside `triggerWindowMs`. */
  triggerMode: z.enum(['any', 'all']).default('any'),
  triggerWindowMs: Milliseconds.default(5000),
  conditions: z.array(Condition).default([]),
  actions: z.array(Action).min(1),
  fireLimit: FireLimit.default({ kind: 'unlimited' }),
  /** Time before the rule may fire again. */
  cooldownMs: Milliseconds.default(0),
})
export type Rule = z.infer<typeof Rule>

/** A manual button on the operator's soundboard. */
export const Cue = z.object({
  id: Id,
  name: z.string().max(60),
  colorHex: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  hotkey: z.string().max(20).optional(),
})
export type Cue = z.infer<typeof Cue>

export const VariableDeclaration = z.object({
  name: z.string().max(64),
  scope: Scope.default('listener'),
  initial: VariableValue,
})
export type VariableDeclaration = z.infer<typeof VariableDeclaration>
